import type React from 'react';
import { useMemo } from 'react';

import type {
  OnChangeFn, RowData, RowSelectionState,
} from '@tanstack/react-table';

import type { SelectCellProps, SelectHeaderProps } from './cell';
import { SelectCell, SelectHeader } from './cell';
import type { TableViewHeadProps } from './head';
import type { TableViewProps } from './TableView';
import { TableView } from './TableView';

import './TableView.sass';

const displayName = 'TableViewSelectable';

export interface TableViewSelectableProps<TData extends RowData> extends TableViewProps<TData> {
  TableHead: React.JSXElementConstructor<TableViewHeadProps<TData>>;
  rowSelection: RowSelectionState;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
}

function TableViewSelectable<TData extends RowData>({
  columns: columnsProp = [],
  'data-testid': dataTestId = undefined,
  rowSelection,
  onRowSelectionChange,
  ...tableViewProps
}: TableViewSelectableProps<TData>): React.JSX.Element {
  const columns = useMemo(() => ([
    {
      id: 'select',
      header: (props: SelectHeaderProps<TData>) => SelectHeader<TData>({
        ...props,
        'data-testid': dataTestId,
      }),
      cell: (props: SelectCellProps<TData>) => SelectCell<TData>({
        ...props,
        'data-testid': dataTestId,
      }),
    },
    ...columnsProp,
  ]), [columnsProp, dataTestId]);

  return (
    <TableView
      columns={columns}
      data-testid={dataTestId}
      enableRowSelection
      onRowSelectionChange={onRowSelectionChange}
      state={{
        rowSelection,
      }}
      {...tableViewProps}
    />
  );
}

TableViewSelectable.displayName = displayName;

export { TableViewSelectable };
